import { FormControl, FormControlLabel, Radio, RadioGroup as MaterialRadioGroup } from "@mui/material";
import React, { useEffect, useRef } from "react";

export type RadioGroupOption = {
    value: string;
    label: string;
}

type RadioGroupProps = {
    className?: string;
    name: string;
    label: string;
    handleValueChange?: (value: any, field?: string) => void,
    handleValidation?: (value: any, field?: string) => void | boolean,
    error?: string | undefined;
    options: RadioGroupOption[];
    defaultValue: string | undefined;
    disabled?: boolean;
    ref?:  React.RefObject<HTMLDivElement | null>;
    isOutsideView?: boolean;
}

export default function RadioGroup({
                                       className,
                                       name,
                                       label,
                                       handleValueChange = () => {},
                                       handleValidation = () => {},
                                       error,
                                       options,
                                       defaultValue,
                                       disabled = false,
                                       ref,
                                       isOutsideView,
                                   }: RadioGroupProps) {
    const hasComponentRenderedInView = useRef(!isOutsideView);
    const hasFieldValidationTriggered = useRef(false);
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleValueChange(event.target.value, name);
        handleValidation(event.target.value, name);
    };

    useEffect(() => {
        if (hasComponentRenderedInView.current) {
            if (!hasFieldValidationTriggered.current && isOutsideView && ref?.current) {
                hasFieldValidationTriggered.current = true;
                handleValidation(defaultValue, name);
            }
        } else {
            hasComponentRenderedInView.current = !isOutsideView;
        }
    }, [isOutsideView]);

    return (
      <div ref={ref}>
          <fieldset className={className}>
              <legend className={`block text-lg font-medium text-gray-700 mb-1 ${disabled && "text-base"}`}>
                  {`${label}:`}
              </legend>
              <FormControl className={`${disabled ? "pointer-events-none" : "pointer-events-auto"}`} error={!!error} component="fieldset" variant="standard">
                  <MaterialRadioGroup row name={name} value={defaultValue} onChange={onChange}>
                      {
                          options.map((option, index) => {
                              return (
                                <FormControlLabel
                                  value={option.value}
                                  control={
                                      <Radio
                                        size="small"
                                        sx={{ color: !!error ? '#EF4444' : (disabled ? '#C2C2C2' : undefined), '&.Mui-checked': { color: '#52AE30'} }}
                                      />
                                  }
                                  sx={{
                                      '& .MuiFormControlLabel-label': {
                                          fontSize: '0.875rem',
                                          color: defaultValue === option.value ? '#000' : (!!error ? '#EF4444' : (disabled ? '#C2C2C2' : undefined)),
                                          opacity: disabled ? 1 : undefined,
                                      },
                                  }}
                                  label={option.label}
                                  key={index}
                                />
                              )
                          })
                      }
                  </MaterialRadioGroup>
                  {error && <p className="text-sm text-red-500 mt-1 ml-1" role="alert" data-field-error="true">{error}</p>}
              </FormControl>
          </fieldset>
      </div>
    )
}
